import { Link } from 'react-router-dom'
import {
  IoCalendarOutline,
  IoTimeOutline,
  IoDocumentTextOutline,
  IoPeopleOutline,
  IoMapOutline,
  IoArrowForwardOutline,
  IoEllipseOutline,
  IoCheckmarkCircleOutline,
  IoBarChartOutline,
  IoOpenOutline,
  IoImageOutline,
} from 'react-icons/io5'
import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { useAppointments } from '../hooks/useAppointments'
import { usePatients } from '../hooks/usePatients'
import { useAuthStore } from '../stores'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { useDashboardStats } from '../hooks/useDashboardStats'
import { useFiles } from '../hooks/useFiles'

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })
const formatTime = (date: string) =>
  new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
const getInitials = (first = '', last = '') => `${last[0] ?? ''}${first[0] ?? ''}`.toUpperCase()

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

const localDayKey = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/* ─── Mini bar chart SVG (hebdomadaire) ─── */
function WeeklyBarChart({ data }: { data: Array<{ date: string; count: number }> }) {
  const max = Math.max(...data.map(d => d.count), 1)
  const W = 280, H = 80, pad = 4, barW = Math.floor((W - pad * (data.length + 1)) / data.length)
  const todayKey = localDayKey(new Date())

  return (
    <svg width='100%' viewBox={`0 0 ${W} ${H + 18}`} className='overflow-visible'>
      {data.map((d, i) => {
        const barH = Math.max(3, (d.count / max) * H)
        const x = pad + i * (barW + pad)
        const y = H - barH
        const isToday = d.date === todayKey
        const parsed = new Date(`${d.date}T12:00:00`)
        return (
          <g key={d.date}>
            <rect x={x} y={y} width={barW} height={barH} rx={3}
              fill={isToday ? 'rgb(99 102 241)' : 'rgb(99 102 241 / 0.35)'}
              className='transition-all duration-500'
            />
            {d.count > 0 && (
              <text x={x + barW / 2} y={y - 3} textAnchor='middle' fontSize={8} fill='rgb(99 102 241)' fontWeight='600'>
                {d.count}
              </text>
            )}
            <text x={x + barW / 2} y={H + 13} textAnchor='middle' fontSize={8}
              fill='currentColor' className='text-gray-400'>
              {DAYS_FR[parsed.getDay()] ?? ''}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export default function Home() {
  const user = useAuthStore(s => s.user)
  const { data: stats, isLoading: loadingStats } = useDashboardStats()

  const rangeParams = useMemo(() => {
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    return {
      limit: 80,
      sortOrder: 'ASC' as const,
      startDate: todayStart.toISOString(),
    }
  }, [])

  const { data: appointmentsData, isLoading: loadingAppts } = useAppointments(rangeParams)
  const { data: patientsData, isLoading: loadingPatients } = usePatients({ limit: 5 })
  const { data: filesData, isLoading: loadingFiles } = useFiles({ limit: 5 })

  const appointments = appointmentsData?.data ?? []
  const patients = patientsData?.data ?? []
  const recentFiles = filesData?.data ?? []

  const todayVisits = useMemo(() => {
    const todayKey = localDayKey(new Date())
    return appointments
      .filter(a => localDayKey(new Date(a.startDate)) === todayKey)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
  }, [appointments])

  const upcoming = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setHours(0, 0, 0, 0)
    tomorrow.setDate(tomorrow.getDate() + 1)
    return appointments
      .filter(a => new Date(a.startDate) >= tomorrow)
      .slice(0, 5)
  }, [appointments])

  const greetHour = new Date().getHours()
  const greet = greetHour < 12 ? 'Bonjour' : greetHour < 18 ? 'Bon après-midi' : 'Bonsoir'

  const statCards = [
    {
      label: "Visites aujourd'hui", value: stats?.todayVisits ?? todayVisits.length,
      icon: IoMapOutline, color: 'primary', to: '/tournee',
    },
    {
      label: 'À venir', value: stats?.upcomingVisits ?? upcoming.length,
      icon: IoTimeOutline, color: 'success', to: '/planning',
    },
    {
      label: 'Patients', value: stats?.totalPatients ?? (patientsData?.pagination?.totalItems ?? 0),
      icon: IoPeopleOutline, color: 'warning', to: '/patients',
    },
    {
      label: 'Documents', value: stats?.totalDocuments ?? 0,
      icon: IoDocumentTextOutline, color: 'danger', to: '/documents/download',
    },
  ]

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-50 dark:bg-primary-600/10 text-primary-600 dark:text-primary-400 border-primary-100 dark:border-primary-500/20',
    success: 'bg-success-50 dark:bg-success-600/10 text-success-600 dark:text-success-500 border-success-100 dark:border-success-500/20',
    warning: 'bg-warning-50 dark:bg-warning-600/10 text-warning-600 dark:text-warning-500 border-warning-100 dark:border-warning-500/20',
    danger: 'bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 border-red-100 dark:border-red-500/20',
  }

  const weeklyData = stats?.weeklyActivity ?? []
  const totalToday = stats?.todayVisits ?? todayVisits.length

  return (
    <div className='p-4 lg:p-6 xl:p-8 min-h-full'>
      {/* Greeting */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className='mb-6'>
        <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
          {greet}, {user?.firstName ?? 'Infirmier'} 👋
        </h2>
        <p className='text-gray-500 dark:text-gray-400 mt-1'>
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </motion.div>

      {/* Stats row */}
      <div className='grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6'>
        {statCards.map(({ label, value, icon: Icon, color, to }, i) => (
          <motion.div key={label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
            <Link to={to} className='block bg-white dark:bg-dark-50 rounded-2xl p-4 lg:p-5 border border-gray-100 dark:border-gray-800 hover:shadow-md hover:border-gray-200 dark:hover:border-gray-700 transition-all group'>
              <div className='flex items-center justify-between mb-3'>
                <div className={`p-2 rounded-xl border ${colorMap[color]}`}>
                  <Icon size={18} />
                </div>
                <IoArrowForwardOutline size={14} className='text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors' />
              </div>
              {loadingStats ? (
                <div className='h-8 flex items-center'><LoadingSpinner size='sm' /></div>
              ) : (
                <div className='text-3xl font-bold text-gray-900 dark:text-white mb-0.5'>{value}</div>
              )}
              <p className='text-xs text-gray-500 dark:text-gray-400'>{label}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Activité */}
      <div className='mb-4'>

        {/* Activité hebdomadaire */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
          className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 p-5'>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2'>
              <IoBarChartOutline className='text-primary-500' size={16} />
              <h3 className='font-semibold text-gray-900 dark:text-white text-sm'>Activité 7 jours</h3>
            </div>
            {!loadingStats && weeklyData.length > 0 && (
              <span className='text-xs text-gray-400'>
                {weeklyData.reduce((s, d) => s + d.count, 0)} visites
              </span>
            )}
          </div>
          {loadingStats ? (
            <div className='flex justify-center items-center h-24'><LoadingSpinner /></div>
          ) : weeklyData.length === 0 ? (
            <div className='flex items-center justify-center h-24 text-gray-300 dark:text-gray-600 text-sm'>Aucune donnée</div>
          ) : (
            /* Largeur contenue : le SVG s'étire proportionnellement, on le garde lisible */
            <div className='max-w-lg mx-auto'>
              <WeeklyBarChart data={weeklyData} />
            </div>
          )}
        </motion.div>

      </div>

      {/* Tournée + agenda */}
      <div className='grid grid-cols-1 xl:grid-cols-3 gap-4'>

        {/* Tournée du jour */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className='xl:col-span-2 bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden'>
          <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800'>
            <div className='flex items-center gap-2'>
              <IoMapOutline className='text-primary-500' size={16} />
              <h3 className='font-semibold text-gray-900 dark:text-white text-sm'>Tournée du jour</h3>
              <span className='ml-1 px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 text-xs font-semibold'>{totalToday}</span>
            </div>
            <Link to='/tournee' className='text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium flex items-center gap-1'>
              Gérer <IoArrowForwardOutline size={12} />
            </Link>
          </div>
          <div className='divide-y divide-gray-50 dark:divide-gray-800'>
            {loadingAppts ? (
              <div className='flex justify-center py-10'><LoadingSpinner /></div>
            ) : todayVisits.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-10 text-gray-400'>
                <IoCalendarOutline size={32} className='mb-3 opacity-50' />
                <p className='text-sm'>Aucune visite aujourd'hui</p>
                <Link to='/add-appointment' className='mt-4 btn-primary text-sm px-4 py-2'>Ajouter un RDV</Link>
              </div>
            ) : (
              todayVisits.map((v, i) => {
                const statusStyle = v.status === 'COMPLETED'
                  ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-500'
                  : v.status === 'IN_PROGRESS'
                    ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-100 dark:border-blue-500/20 text-blue-400'
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400'
                return (
                  <motion.div key={v.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.42 + i * 0.04 }}
                    className='flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors'>
                    <div className='w-8 h-8 rounded-full bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/20 flex items-center justify-center flex-shrink-0'>
                      <span className='text-xs font-bold text-primary-600 dark:text-primary-400'>
                        {getInitials(v.patient?.firstName, v.patient?.lastName)}
                      </span>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='font-semibold text-gray-900 dark:text-white text-sm truncate'>
                        {v.patient ? `${(v.patient.lastName ?? '').toUpperCase()} ${v.patient.firstName ?? ''}`.trim() : 'Patient inconnu'}
                      </p>
                      <p className='text-xs text-gray-400 mt-0.5 truncate'>{v.patient?.disease ?? ''}</p>
                    </div>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                      <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>{formatTime(v.startDate)}</span>
                      <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${statusStyle}`}>
                        {v.status === 'COMPLETED' ? <IoCheckmarkCircleOutline size={12} /> : <IoEllipseOutline size={12} />}
                      </span>
                    </div>
                  </motion.div>
                )
              })
            )}
          </div>
        </motion.div>

        {/* Right column */}
        <div className='flex flex-col gap-4'>
          {/* Prochains RDV */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.44 }}
            className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden'>
            <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <IoCalendarOutline className='text-success-600' size={14} />
                <h3 className='font-semibold text-gray-900 dark:text-white text-sm'>Prochains RDV</h3>
              </div>
              <Link to='/planning' className='text-xs text-success-600 dark:text-success-500 hover:underline font-medium'>Voir tout</Link>
            </div>
            <div className='divide-y divide-gray-50 dark:divide-gray-800'>
              {loadingAppts ? (
                <div className='flex justify-center py-6'><LoadingSpinner size='sm' /></div>
              ) : upcoming.length === 0 ? (
                <p className='text-center text-sm text-gray-400 py-6'>Aucun rendez-vous à venir</p>
              ) : upcoming.map(a => (
                <div key={a.id} className='flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors'>
                  <div className='w-1.5 h-1.5 rounded-full bg-success-500 flex-shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-gray-900 dark:text-white truncate'>
                      {a.patient ? `${a.patient.lastName} ${a.patient.firstName}` : 'Patient inconnu'}
                    </p>
                    <p className='text-xs text-gray-400'>{formatDate(a.startDate)} · {formatTime(a.startDate)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Patients récents */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48 }}
            className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden'>
            <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <IoPeopleOutline className='text-warning-600' size={14} />
                <h3 className='font-semibold text-gray-900 dark:text-white text-sm'>Patients récents</h3>
              </div>
              <Link to='/patients' className='text-xs text-warning-600 dark:text-warning-500 hover:underline font-medium'>Voir tout</Link>
            </div>
            <div className='divide-y divide-gray-50 dark:divide-gray-800'>
              {loadingPatients ? (
                <div className='flex justify-center py-6'><LoadingSpinner size='sm' /></div>
              ) : patients.length === 0 ? (
                <p className='text-center text-sm text-gray-400 py-6'>Aucun patient</p>
              ) : patients.map(p => (
                <Link key={p.id} to={`/patients/${p.id}`}
                  className='flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors'>
                  <div className='w-8 h-8 rounded-full bg-warning-50 dark:bg-warning-600/10 border border-warning-100 dark:border-warning-500/20 flex items-center justify-center flex-shrink-0'>
                    <span className='text-xs font-bold text-warning-600 dark:text-warning-500'>{getInitials(p.firstName, p.lastName)}</span>
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-gray-900 dark:text-white truncate'>{p.lastName?.toUpperCase()} {p.firstName}</p>
                    <p className='text-xs text-gray-400 truncate'>{p.disease ?? 'Aucune pathologie'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Derniers documents */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.52 }}
            className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden'>
            <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <IoDocumentTextOutline className='text-blue-500' size={14} />
                <h3 className='font-semibold text-gray-900 dark:text-white text-sm'>Derniers documents</h3>
              </div>
              <Link to='/documents/download' className='text-xs text-blue-500 dark:text-blue-400 hover:underline font-medium flex items-center gap-1'>
                Voir tout <IoArrowForwardOutline size={11} />
              </Link>
            </div>
            <div className='divide-y divide-gray-50 dark:divide-gray-800'>
              {loadingFiles ? (
                <div className='flex justify-center py-6'><LoadingSpinner size='sm' /></div>
              ) : recentFiles.length === 0 ? (
                <p className='text-center text-sm text-gray-400 py-6'>Aucun document</p>
              ) : recentFiles.map(f => {
                const ext = f.name.split('.').pop()?.toLowerCase() ?? ''
                const isImage = f.mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                return (
                  <Link key={f.id} to='/documents/download'
                    className='flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors'>
                    <div className='w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 flex items-center justify-center flex-shrink-0'>
                      {isImage
                        ? <IoImageOutline size={14} className='text-blue-500' />
                        : <IoDocumentTextOutline size={14} className='text-blue-500' />}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm font-medium text-gray-900 dark:text-white truncate'>{f.name}</p>
                      <p className='text-xs text-gray-400'>{new Date(f.createdAt).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <IoOpenOutline size={14} className='text-gray-300 dark:text-gray-600 flex-shrink-0' />
                  </Link>
                )
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
